/* eslint-disable max-statements */
import { useCallback, useMemo } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import isEqual from 'lodash.isequal';
import { useSelector } from 'react-redux';
import { PaginationResponseDto } from '@tiendanube/common';
import { Action } from '@tiendanube/common/src/enums';
import { useToastProgress } from 'App/components/ToastProgressContext';
import { useAppDispatch } from 'App/store';
import { useAsyncFunc, useListFilters, useToast } from 'commons/hooks';
import { getTodayIsoString, subtractDays } from 'commons/utils/date';
import { useBulkActions } from 'domains/Catalog/Products/pages/ProductListPage/components';
import { useSavedSearches } from 'domains/Orders/components/SavedSearches';
import {
  ORDERS_PAGE_LIMIT,
  ORDERS_PER_PAGE,
} from 'domains/Orders/Orders/pages/constants';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import {
  defaultFilters,
  FiltersParamsType,
  FiltersType,
  InterfaceOrderUpdateParams,
  statusFilter,
} from '../../ordersService';
import {
  fetchMoreOrders,
  fetchOrders,
  updateOrders as updateOrdersAction,
  fetchStockIssuesCountOrders,
  getStockIssuesOrdersCount,
} from '../../ordersSlice';
import {
  getAppliedFilters,
  getOrdersCount,
  getOrdersIds,
  getOrdersPagination,
  getOrdersStatus,
  getOrdersStatusSuccess,
} from '../../ordersSlice/ordersSelectors';
import { useGetTotalOpenedOrders } from '../useGetTotalOpenedOrders';

interface UseOrdersListResult {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  statusSuccess: string;
  ordersCount: number;
  totalOpenOrders: number;
  ordersIds: string[];
  pagination: PaginationResponseDto;
  filters: FiltersType;
  appliedFilters: FiltersType;
  hasFilters: boolean;
  getOrdersList: (filters?: FiltersParamsType) => Promise<void>;
  refreshOrdersList: () => Promise<void>;
  getMoreOrders: () => Promise<void>;
  goToPage: (page: number) => Promise<void>;
  updateOrders: (params: InterfaceOrderUpdateParams) => Promise<void>;
  changeFilters: (filters: FiltersParamsType) => void;
  removeMetafieldsFilters: () => void;
  fetchStockIssuesCount: () => Promise<void>;
  hasStockIssues: boolean;
}

const TYPE_FETCH_ORDERS = 'advanced';

function useOrdersList(): UseOrdersListResult {
  const dispatch = useAppDispatch();
  const t = useTranslationOrders();
  const { addToast } = useToast();
  const { addToastProgress, closeToastProgress } = useToastProgress();
  const { filters, hasFilters, changeFilters, removeMetafieldsFilters } =
    useListFilters('orders', defaultFilters);
  const { isLoading, isSuccess, isError } = useSelector(getOrdersStatus);
  const statusSuccess = useSelector(getOrdersStatusSuccess);
  const ordersCount = useSelector(getOrdersCount);
  const ordersIds = useSelector(getOrdersIds);
  const pagination = useSelector(getOrdersPagination);
  const { setDisabled } = useBulkActions();
  const appliedFilters = useSelector(getAppliedFilters);
  const stockIssuesCount = useSelector(getStockIssuesOrdersCount);
  const { totalOpenedOrders: totalOpenOrders } = useGetTotalOpenedOrders();
  const { fetchSavedSearchesCount } = useSavedSearches();

  // Wrapper to default some behavior of changeFilters
  const handleChangeFilters = useCallback(
    (customFilters: FiltersParamsType, updateURL = true) => {
      // Default page parameters if not provided
      const fetchFilters = {
        page: 1,
        perPage: ORDERS_PER_PAGE.toString(),
        ...customFilters,
      };
      const filtersDigest = Object.fromEntries(
        Object.entries(fetchFilters).filter(
          ([key, value]) =>
            (value !== 'all' && value !== 'open') ||
            (value === 'all' && key === statusFilter),
        ),
      ) as FiltersType;
      // Parsing perPage to string since upon initial page loading
      // somehow perPage is mapped as a Number instead of a String.
      // Could not find the root cause of this issue, but allowing it
      // causes an initial double fetch (since then URL params are not exactly equals (string vs number))
      const filtersChanged = !isEqual(filtersDigest, {
        ...filters,
        perPage: filters.perPage?.toString(),
      });
      if (filtersChanged) {
        changeFilters(filtersDigest, updateURL);
      }
      return fetchFilters;
    },
    [changeFilters, filters],
  );

  const dateFrom: string = useMemo(() => {
    const sevenDaysAgo = subtractDays(getTodayIsoString(), 7);
    const timestamp = localStorage.getItem('orders-list-stock-issues-ts');

    if (
      timestamp !== null &&
      new Date(timestamp).getTime() > new Date(sevenDaysAgo).getTime()
    ) {
      return timestamp;
    }
    return sevenDaysAgo;
  }, []);

  const fetchStockIssuesCount = useCallback(async () => {
    await dispatch(fetchStockIssuesCountOrders(dateFrom)).unwrap();
  }, [dispatch, dateFrom]);

  const hasStockIssues = stockIssuesCount > 0;

  const getOrdersList = useCallback(
    async (customFilters?: FiltersParamsType) => {
      // Only called from URL handler, don't update attempt to update the URL again
      const fetchFilters = handleChangeFilters(customFilters ?? filters, false);
      await dispatch(
        fetchOrders({
          filters: fetchFilters,
          type: TYPE_FETCH_ORDERS,
        }),
      );
    },
    [filters, handleChangeFilters, dispatch],
  );

  const refreshOrdersList = useCallback(async () => {
    const filtersWithFirstPage = {
      ...filters,
      page: 1,
      perPage: ORDERS_PER_PAGE.toString(),
    };
    handleChangeFilters(filtersWithFirstPage);
    await dispatch(
      fetchOrders({ filters: filtersWithFirstPage, type: TYPE_FETCH_ORDERS }),
    );
  }, [filters, handleChangeFilters, dispatch]);

  const getMoreOrders = useCallback(async () => {
    if (ordersIds.length !== filters.page * ORDERS_PER_PAGE) return;
    const newFilters = {
      ...filters,
      page: filters.page + 1,
      perPage: ORDERS_PER_PAGE.toString(),
    };
    // Don't update URL for mobile fetching, since it would trigger
    // a rerender of the component and both reset the scroll position
    // and make 2 order fetches (the other would come from the URL change)
    handleChangeFilters(newFilters, false);
    await dispatch(
      fetchMoreOrders({ filters: newFilters, type: TYPE_FETCH_ORDERS }),
    );
  }, [dispatch, handleChangeFilters, filters, ordersIds.length]);

  const goToPage = useCallback(
    async (page: number) => {
      if (filters.page === page) return;
      const newFilters = {
        ...filters,
        page,
        perPage: ORDERS_PER_PAGE.toString(),
      };
      if (page <= ORDERS_PAGE_LIMIT + 1) {
        // Updates URL and fetches orders
        changeFilters(newFilters);
      }
    },
    [changeFilters, filters],
  );

  const updateOrdersError = (params) => {
    setDisabled(false);
    showFeedbackToast('error', params?.actionType);
  };

  const [updateOrders] = useAsyncFunc<InterfaceOrderUpdateParams, void>(
    async (params) => {
      if (!params) return;
      showFeedbackToast('loading', params.actionType);
      const result = unwrapResult(await dispatch(updateOrdersAction(params)));
      showFeedbackToast('success', params.actionType);
      return result;
    },
    () => {
      Promise.all([getOrdersList(), fetchSavedSearchesCount()]);
    },
    updateOrdersError,
  );

  const showFeedbackErrorToast = (action) => {
    const errorMessage =
      action && [Action.ARCHIVE, Action.CANCEL].includes(action)
        ? action
        : 'default';
    addToast({
      label: t(`bulkActions.toast.error.${errorMessage}`),
      appearance: 'danger',
    });
  };

  const showFeedbackToast = (
    type: 'loading' | 'success' | 'error',
    action?: Action,
  ) => {
    if (type === 'loading') {
      addToastProgress({ label: t(`bulkActions.toast.loading.${action}`) });
    } else {
      closeToastProgress();
      if (type === 'success')
        addToast({
          label: t(`bulkActions.toast.success.${action}`),
          appearance: 'success',
        });
      else {
        showFeedbackErrorToast(action);
      }
    }
  };

  return {
    isLoading,
    isSuccess,
    isError,
    statusSuccess,
    ordersCount,
    totalOpenOrders,
    ordersIds,
    pagination,
    filters,
    appliedFilters,
    hasFilters,
    getOrdersList,
    refreshOrdersList,
    getMoreOrders,
    goToPage,
    updateOrders,
    changeFilters: handleChangeFilters,
    removeMetafieldsFilters,
    fetchStockIssuesCount,
    hasStockIssues,
  };
}

export default useOrdersList;
